import React from 'react';
import { Layout } from '../components/layout/Layout';
import { PageHero } from '../components/shared';
import { motion } from 'framer-motion';
import { kontaktDaten, pageHeroImages } from '../data/mock';
import { ExternalLink } from 'lucide-react';

export const ImpressumPage = () => {
  return (
    <Layout>
      <PageHero
        title="Impressum"
        subtitle="Angaben gemäß § 5 TMG"
        backgroundImage={pageHeroImages.impressum}
        showCTA={false}
      />

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 lg:px-8 max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="prose prose-lg max-w-none"
          >
            {/* Anschrift */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-[#0b382a] mb-4">Anschrift</h2>
              <p className="text-[#0b382a]/80">
                {kontaktDaten.praxisName}<br />
                {kontaktDaten.adresse.strasse}<br />
                {kontaktDaten.adresse.plz} {kontaktDaten.adresse.ort}
              </p>
            </div>

            {/* Vertreten durch */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-[#0b382a] mb-4">Vertreten durch</h2>
              <p className="text-[#0b382a]/80">
                <strong>Dr. med. Mischa von Bergh</strong><br />
                Facharzt für Allgemeinmedizin
              </p>
              <p className="text-[#0b382a]/80 mt-2">
                <strong>Dr. med. Martin von Bergh, MBA</strong><br />
                Facharzt für Allgemeinmedizin, Notfallmedizin
              </p>
            </div>

            {/* Kontakt */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-[#0b382a] mb-4">Kontakt</h2>
              <p className="text-[#0b382a]/80">
                Telefon: {kontaktDaten.telefon}<br />
                Telefax: {kontaktDaten.fax}<br />
                E-Mail: <a href={`mailto:${kontaktDaten.email}`} className="text-[#b5a05f] hover:underline">{kontaktDaten.email}</a>
              </p>
            </div>

            {/* EU-Streitschlichtung */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-[#0b382a] mb-4">EU-Streitschlichtung</h2>
              <p className="text-[#0b382a]/80">
                Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:{' '}
                <a href="https://ec.europa.eu/consumers/odr/" target="_blank" rel="noopener noreferrer" className="text-[#b5a05f] hover:underline inline-flex items-center gap-1">
                  https://ec.europa.eu/consumers/odr/
                  <ExternalLink className="w-4 h-4" />
                </a>
                <br />Unsere E-Mail-Adresse finden Sie oben im Impressum.
              </p>
            </div>

            {/* Verbraucherstreitbeilegung */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-[#0b382a] mb-4">Verbraucherstreitbeilegung / Universalschlichtungsstelle</h2>
              <p className="text-[#0b382a]/80">
                Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.
              </p>
            </div>

            {/* Aufsichtsbehörde */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-[#0b382a] mb-4">Aufsichtsbehörde</h2>
              <p className="text-[#0b382a]/80">
                Kassenärztliche Vereinigung – Rheinland-Pfalz<br />
                Isaac-Fulda-Allee 14<br />
                55124 Mainz<br />
                Tel.: 06131 / 326-326<br />
                Fax: 06131 / 326-327<br />
                <a href="https://www.kv-rlp.de/" target="_blank" rel="noopener noreferrer" className="text-[#b5a05f] hover:underline inline-flex items-center gap-1">
                  https://www.kv-rlp.de/
                  <ExternalLink className="w-4 h-4" />
                </a>
              </p>
            </div>

            {/* Berufsbezeichnung */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-[#0b382a] mb-4">Berufsbezeichnung und berufsrechtliche Regelungen</h2>
              <p className="text-[#0b382a]/80 mb-4">
                <strong>Berufsbezeichnung:</strong> Arzt<br />
                <strong>Zuständige Kammer:</strong> Landesärztekammer Rheinland-Pfalz
              </p>
              <p className="text-[#0b382a]/80 mb-4">
                Postfach 29 26<br />
                55019 Mainz<br />
                Tel.: 06131 / 28822-0<br />
                Fax: 06131 / 28822-88<br />
                Web: <a href="https://www.laek-rlp.de/" target="_blank" rel="noopener noreferrer" className="text-[#b5a05f] hover:underline inline-flex items-center gap-1">
                  https://www.laek-rlp.de/
                  <ExternalLink className="w-4 h-4" />
                </a>
              </p>
              <p className="text-[#0b382a]/80 mb-2">
                <strong>Verliehen in:</strong> Deutschland
              </p>
              <p className="text-[#0b382a]/80">
                <strong>Es gelten folgende berufsrechtliche Regelungen:</strong>
              </p>
              <ul className="list-disc pl-6 text-[#0b382a]/80 space-y-1">
                <li>
                  <a href="https://www.laekh.de/images/Aerzte/Rund_ums_Recht/Rechtsquellen/heilberufsgesetz.pdf" target="_blank" rel="noopener noreferrer" className="text-[#b5a05f] hover:underline">
                    Heilberufsgesetz
                  </a>
                </li>
                <li>
                  <a href="https://www.laekh.de/images/Aerzte/Rund_ums_Recht/Rechtsquellen/berufsordnung.pdf" target="_blank" rel="noopener noreferrer" className="text-[#b5a05f] hover:underline">
                    Berufsordnung
                  </a>
                </li>
              </ul>
            </div>

            {/* Quellnachweise */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-[#0b382a] mb-4">Quellnachweise der Bilder</h2>
              <p className="text-[#0b382a]/80">
                MRT – Icon in Footerarea: Radiology icons created by Freepik – Flaticon
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default ImpressumPage;
